import type {
  ChapterRef,
  NavigationTarget,
  ReaderStreamSequence,
  ReaderStreamSnapshot,
  StreamChapterItem,
  StreamNavigationRequest,
  VerseHighlight,
} from './types';

export class ReaderStreamState {
  sequence = $state.raw<ReaderStreamSequence>({
    kind: 'fixed-list',
    title: null,
    source: null,
    items: [],
  });
  currentItemIndex = $state<number | null>(null);
  visible = $state.raw(new Set<number>());
  scrollTop = $state(0);
  activeVerseHighlight = $state<VerseHighlight | null>(null);
  navigationRequest = $state<StreamNavigationRequest | null>(null);

  private searchHighlightNonce = 0;
  private highlightClearHandle: ReturnType<typeof setTimeout> | null = null;
  private navigationNonce = 0;

  constructor(
    readonly id: string,
    private readonly chapterRefsFor: () => ChapterRef[],
    private readonly ensureChapterLoaded: (index: number) => Promise<void>,
    private readonly onStateChanged: () => void,
    private readonly onVisibilityChanged: () => void,
    initialSequence: ReaderStreamSequence,
    initialItemIndex: number,
    initialScrollTop = 0,
  ) {
    this.sequence = initialSequence;
    this.currentItemIndex = initialItemIndex;
    this.scrollTop = initialScrollTop;
  }

  get currentItem(): StreamChapterItem | null {
    const index = this.currentItemIndex;
    return index === null ? null : (this.sequence.items[index] ?? null);
  }

  get currentCatalogIndex(): number | null {
    return this.currentItem?.catalogIndex ?? null;
  }

  get currentReference(): ChapterRef | null {
    const catalogIndex = this.currentCatalogIndex;
    return catalogIndex === null ? null : (this.chapterRefsFor()[catalogIndex] ?? null);
  }

  destroy(): void {
    if (this.highlightClearHandle !== null) {
      clearTimeout(this.highlightClearHandle);
      this.highlightClearHandle = null;
    }
  }

  replaceSequence(sequence: ReaderStreamSequence, itemIndex: number, scrollTop = 0): void {
    this.sequence = sequence;
    this.currentItemIndex = itemIndex;
    this.visible = new Set();
    this.scrollTop = scrollTop;
    this.navigationRequest = null;
    this.primeVisibleWindow(itemIndex);
    this.onStateChanged();
  }

  activateItemIndex(index: number): void {
    if (!this.sequence.items[index]) return;

    this.currentItemIndex = index;
    this.primeVisibleWindow(index);
    this.onStateChanged();
  }

  activateFromScroll(index: number): void {
    if (index === this.currentItemIndex) return;

    if (!this.sequence.items[index]) return;

    this.currentItemIndex = index;
    this.onStateChanged();
  }

  setVisible(indices: Iterable<number>): void {
    const nextVisible = new Set(indices);
    this.visible = nextVisible;

    for (const index of nextVisible) {
      void this.ensureItemLoaded(index);
    }
    this.onVisibilityChanged();
  }

  primeVisibleWindow(index: number, radius = 1): void {
    const nextVisible = new Set(this.visible);
    for (const visibleIndex of this.windowIndices(index, radius)) {
      nextVisible.add(visibleIndex);
    }

    this.visible = nextVisible;
    for (const visibleIndex of nextVisible) {
      void this.ensureItemLoaded(visibleIndex);
    }
    this.onVisibilityChanged();
  }

  async ensureItemLoaded(index: number): Promise<void> {
    const catalogIndex = this.sequence.items[index]?.catalogIndex;
    if (catalogIndex === undefined) {
      return;
    }

    await this.ensureChapterLoaded(catalogIndex);
  }

  requestNavigation(
    target: NavigationTarget,
    behavior: ScrollBehavior,
    options: { restoreScrollTop?: number | null } = {},
  ): void {
    this.navigationRequest = {
      nonce: ++this.navigationNonce,
      target,
      behavior,
      restoreScrollTop: options.restoreScrollTop ?? null,
    };
  }

  setScrollTop(value: number): void {
    this.scrollTop = value;
  }

  highlightVerse(bookId: number, chapter: number, verse: number): void {
    if (this.highlightClearHandle !== null) {
      clearTimeout(this.highlightClearHandle);
    }

    this.activeVerseHighlight = {
      bookId,
      chapter,
      verse,
      nonce: ++this.searchHighlightNonce,
    };

    this.highlightClearHandle = setTimeout(() => {
      if (
        this.activeVerseHighlight?.bookId === bookId &&
        this.activeVerseHighlight?.chapter === chapter &&
        this.activeVerseHighlight?.verse === verse
      ) {
        this.activeVerseHighlight = null;
      }
      this.highlightClearHandle = null;
    }, 5200);
  }

  highlightedVerseForChapter(reference: ChapterRef): number | null {
    const highlight = this.activeVerseHighlight;
    if (!highlight) return null;

    return highlight.bookId === reference.bookId && highlight.chapter === reference.chapter
      ? highlight.verse
      : null;
  }

  highlightNonceForChapter(reference: ChapterRef): number {
    const highlight = this.activeVerseHighlight;
    if (!highlight) return 0;

    return highlight.bookId === reference.bookId && highlight.chapter === reference.chapter
      ? highlight.nonce
      : 0;
  }

  snapshot(): ReaderStreamSnapshot | null {
    const reference = this.currentReference;
    const currentItemIndex = this.currentItemIndex;
    if (!reference || currentItemIndex === null) {
      return null;
    }

    return {
      id: this.id,
      bookId: reference.bookId,
      chapter: reference.chapter,
      currentItemIndex,
      sequence: {
        kind: this.sequence.kind,
        title: this.sequence.title,
        source: this.sequence.source,
        items:
          this.sequence.kind === 'fixed-list'
            ? this.sequence.items.map((item) => ({
                bookId: item.bookId,
                chapter: item.chapter,
                label: item.label ?? null,
              }))
            : undefined,
      },
      scrollTop: this.scrollTop,
    };
  }

  private windowIndices(center: number, radius: number): number[] {
    const chapterCount = this.sequence.items.length;
    if (this.sequence.kind === 'fixed-list') {
      return Array.from({ length: chapterCount }, (_, index) => index);
    }

    const indices: number[] = [];

    for (let index = center - radius; index <= center + radius; index += 1) {
      if (index >= 0 && index < chapterCount) {
        indices.push(index);
      }
    }

    return indices;
  }
}
